import { Cancel, Download, History, ListAlt, PlaylistAdd, Restore } from '@mui/icons-material';
import { Box, Button, Checkbox, FormControl, FormControlLabel, Grid, IconButton, Link, Menu, MenuItem, Paper, Tooltip, Typography } from '@mui/material';
import _ from 'lodash';
import { FC, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import theme from '../../../Theme';
import { usePrompt } from '../../../Views';
import { AffectedDispatchesDialog } from '../../../Views/Dispatch/Components/AffectedDispatchesDialog';
import { QuoteLineItemsOrderView } from '../../../Views/Orders';
import { OrderDto, OrderLineItemDto, ReviewStatus } from '../../../dtos';
import {
    useArchiveOrderDeleteByIdMutation,
    useCreateOrderDownloadSelectedOrdersMutation,
    usePatchOrderCancelMutation,
    usePatchOrderLineItemCancelMutation,
    usePatchOrderLineItemRestoreByIdMutation,
    usePatchOrderRestoreByIdMutation,
} from '../../../store/generated/generatedApi';
import { exportToPdf, LineItemTypes, QuoteType, toEasternDateKey } from '../../../util';
import { ContactSelect } from '../../CommonInputs';
import { ConfirmationDialogWithInput } from '../../Core/ConfirmDialog/ConfirmationDialogWithInput';
import { SlimFormHeader } from '../../Core/SlimFormHeader';
import { AuthenticatedComponent, FormInput, IFormProps, LoadingIndicator, StandardCardWithHeader } from '../../CoreLib/library';
import { OrderLineItemTable } from './OrderLineItemTable';
import { OrderRelatedLinks } from './OrderRelatedLinks';
import { QuickAddMaterialLineItemDialog } from './QuickAddMaterialLineItemDialog/QuickAddMaterialLineItemDialog';
import { OrderHistoryDialog } from './OrderHistoryDialog';
import { useOrderForm } from './useOrderForm';

export interface IOrderFormProps extends IFormProps<OrderDto> {
    jobId: string;
}

export const OrderForm: FC<IOrderFormProps> = (props) => {
    const { isLoading, initValues, jobId } = props;
    const navigate = useNavigate();
    const {
        isFormDirty,
        handleSave,
        handleCancel,
        handleOrderLineItemsChange,
        handleIsActiveChange,
        handleAddNewLineItem,
        handleAddNewLineItems,
        handleRemoveLineItem,
        handleContactChange,
        handleOrderedByContactChange,
        handleIsTaxableChange,
        handlePoNumberChange,
        handleJobNumberChange,
        formContact,
        formOrderedByContact,
        formOrderNumber,
        formOrderDate,
        formOrderLineItems,
        formIsActive,
        formPoNumber,
        formJobNumber,
        fieldErrors,
        formQuote,
        formCustomer,
        formIsTaxable,
        job,
        isJobLoading,
        lineItemFieldErrors,
        isQuickAddMaterialLineItemDialogOpen,
        setIsQuickAddMaterialLineItemDialogOpen,
        selectedSiteHaulingLineItem,
        affectedDispatches,
        isGettingAffectedDispatches,
        showAffectedDispatchesDialog,
        setShowAffectedDispatchesDialog,
        getExcludedMaterialIdsForHaulingLineItem,
        formConcurrencyVersion,
        adoptVersionFromOwnChange
    } = useOrderForm(props, jobId);

    usePrompt('Are you sure you want to leave this page?\nUnsaved changes will be lost.', isFormDirty());

    const [addMenuAnchorEl, setAddMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [addMenuOpen, setAddMenuOpen] = useState(false);
    const [isCancelMenuOpen, setIsCancelMenuOpen] = useState(false);
    const [isCancelOrderLineItemMenuOpen, setIsCancelOrderLineItemMenuOpen] = useState(false);
    const [orderLineItemIndexToModify, setOrderLineItemIndexToModify] = useState<number | null>(null);
    const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false);

    const [cancelOrder, { isLoading: isCancelingOrder }] = usePatchOrderCancelMutation();
    const [cancelOrderLineItem, { isLoading: isCancelingOrderLineItem }] = usePatchOrderLineItemCancelMutation();
    const [restoreOrder, { isLoading: isRestoringOrder }] = usePatchOrderRestoreByIdMutation();
    const [restoreOrderLineItem, { isLoading: isRestoringOrderLineItem }] = usePatchOrderLineItemRestoreByIdMutation();
    const [downloadSelectedPdfs, { isLoading: isDownloadingPdfs }] = useCreateOrderDownloadSelectedOrdersMutation();

    const handleAffectedDispatchesDialogSuccess = useCallback(() => {
        setShowAffectedDispatchesDialog(false);
        handleSave();
    }, [setShowAffectedDispatchesDialog, handleSave]);

    const handleAffectedDispatchesDialogCancel = useCallback(() => {
        setShowAffectedDispatchesDialog(false);
        handleSave();
    }, [setShowAffectedDispatchesDialog, handleSave]);

    const handleSaveClicked = useCallback(() => {
        handleSave();
    }, [handleSave]);

    const currentOrderId = useMemo(() => {
        return initValues?.id;
    }, [initValues?.id]);

    const handleAddMenuOpen = useCallback((event?: React.MouseEvent<HTMLButtonElement>) => {
        if (event) {
            setAddMenuAnchorEl(event.currentTarget);
            setAddMenuOpen(true);
        }
    }, []);

    const handleCancelOrder = useCallback(
        (cancellationReason: string) => {
            setIsCancelMenuOpen(false);
            if (currentOrderId) {
                cancelOrder({ id: currentOrderId, reason: cancellationReason, concurrencyVersion: formConcurrencyVersion }).then((response) => {
                    if ('data' in response) {
                        adoptVersionFromOwnChange(response.data?.concurrencyVersion);
                    }
                });
            }
        },
        [cancelOrder, currentOrderId, formConcurrencyVersion, adoptVersionFromOwnChange]
    );

    const handleRestoreOrder = useCallback(() => {
        if (currentOrderId) {
            restoreOrder({ id: currentOrderId, concurrencyVersion: formConcurrencyVersion }).then((response) => {
                if ('data' in response) {
                    adoptVersionFromOwnChange(response.data?.concurrencyVersion);
                }
            });
        }
    }, [currentOrderId, restoreOrder, formConcurrencyVersion, adoptVersionFromOwnChange]);

    const handleCancelOrderLineItem = useCallback(
        (cancellationReason: string) => {
            setIsCancelOrderLineItemMenuOpen(false);
            if (orderLineItemIndexToModify !== null) {
                var updatedLineItems = _.cloneDeep(formOrderLineItems);
                var updatedLineItem = { ...formOrderLineItems[orderLineItemIndexToModify] };
                if (updatedLineItem) {
                    cancelOrderLineItem({ id: updatedLineItem.id, reason: cancellationReason, concurrencyVersion: formConcurrencyVersion }).then((response) => {
                        const data = (response as any).data as OrderLineItemDto;
                        if (data) {
                            adoptVersionFromOwnChange(data.orderConcurrencyVersion);
                            setOrderLineItemIndexToModify(null);

                            updatedLineItem['canceledOn'] = data.canceledOn;
                            updatedLineItem['canceledByUserId'] = data.canceledByUserId;
                            updatedLineItem['canceledByUser'] = data.canceledByUser;
                            updatedLineItem['cancellationReason'] = data.cancellationReason;
                            updatedLineItem['isCancelable'] = data.isCancelable;

                            updatedLineItems.splice(orderLineItemIndexToModify, 1, updatedLineItem);
                            handleOrderLineItemsChange(updatedLineItems);
                        }
                    });
                }
            }
        },
        [cancelOrderLineItem, formOrderLineItems, handleOrderLineItemsChange, orderLineItemIndexToModify, formConcurrencyVersion, adoptVersionFromOwnChange]
    );

    const handleRestoreOrderLineItem = useCallback(() => {
        if (orderLineItemIndexToModify !== null) {
            var updatedLineItems = _.cloneDeep(formOrderLineItems);
            var updatedLineItem = { ...formOrderLineItems[orderLineItemIndexToModify] };
            if (updatedLineItem) {
                restoreOrderLineItem({ id: updatedLineItem.id, concurrencyVersion: formConcurrencyVersion }).then((response) => {
                    const data = (response as any).data as OrderLineItemDto;
                    if (data) {
                        adoptVersionFromOwnChange(data.orderConcurrencyVersion);
                        setOrderLineItemIndexToModify(null);

                        updatedLineItem['canceledOn'] = data.canceledOn;
                        updatedLineItem['canceledByUserId'] = data.canceledByUserId;
                        updatedLineItem['cancellationReason'] = data.cancellationReason;
                        updatedLineItem['isCancelable'] = data.isCancelable;

                        updatedLineItems.splice(orderLineItemIndexToModify, 1, updatedLineItem);
                        handleOrderLineItemsChange(updatedLineItems);
                    }
                });
            }
        }
    }, [formOrderLineItems, handleOrderLineItemsChange, orderLineItemIndexToModify, restoreOrderLineItem, formConcurrencyVersion, adoptVersionFromOwnChange]);

    const handleCreate = useCallback(
        (type: string) => {
            if (type === LineItemTypes.Material) {
                navigate(`/quote/${formQuote?.id}/lineItem/material/create/${job?.id}/${currentOrderId ?? ''}`);
            } else if (type === LineItemTypes.SiteHauling) {
                navigate(`/quote/${formQuote?.id}/lineItem/siteHauling/create/${job?.id}/${currentOrderId ?? ''}`);
            } else if (type === LineItemTypes.Hourly) {
                navigate(`/quote/${formQuote?.id}/lineItem/hourly/create/${job?.id}/${currentOrderId ?? ''}`);
            } else if (type === LineItemTypes.Dump) {
                navigate(`/quote/${formQuote?.id}/lineItem/dump/create/${job?.id}/${currentOrderId ?? ''}`);
            } else if (type === LineItemTypes.Misc) {
                navigate(`/quote/${formQuote?.id}/lineItem/misc/create/${job?.id}/${currentOrderId ?? ''}`);
            }
        },
        [navigate, formQuote?.id, job?.id, currentOrderId]
    );

    const handleDownload = useCallback(() => {
        downloadSelectedPdfs({
            orderIds: [initValues?.id!],
            siteId: undefined,
            orderDate: undefined
        }).then((response: any) => {
            if (response.data) {
                exportToPdf(response.data, `Order-${formOrderNumber}-${toEasternDateKey(formOrderDate ?? new Date())}.pdf`.replace(/\s+/g, ''));
            }
        })
    }, [downloadSelectedPdfs, formOrderNumber, initValues?.id, formOrderDate]);

    const renderAddMenu = useCallback(() => {
        return (
            <Menu
                anchorEl={addMenuAnchorEl}
                open={addMenuOpen}
                onClose={() => {
                    setAddMenuOpen(false);
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}>
                <AuthenticatedComponent
                    requiredPermissions={['create:lineItem']}
                    children={
                        <>
                            <MenuItem onClick={() => handleCreate(LineItemTypes.Material)}>Material</MenuItem>
                            <MenuItem onClick={() => handleCreate(LineItemTypes.SiteHauling)}>Haul Rate</MenuItem>
                            <MenuItem onClick={() => handleCreate(LineItemTypes.Hourly)}>Hourly</MenuItem>
                            <MenuItem onClick={() => handleCreate(LineItemTypes.Dump)}>Dump</MenuItem>
                            <MenuItem onClick={() => handleCreate(LineItemTypes.Misc)}>Misc</MenuItem>
                        </>
                    }
                />
            </Menu>
        );
    }, [addMenuAnchorEl, addMenuOpen, handleCreate]);

    const isNotModifiable = useMemo(
        () =>
            isCancelingOrder ||
            isCancelingOrderLineItem ||
            isRestoringOrder ||
            isRestoringOrderLineItem ||
            !!initValues?.orderReview ||
            !!initValues?.canceledOn,
        [initValues?.canceledOn, initValues?.orderReview, isCancelingOrder, isCancelingOrderLineItem, isRestoringOrder, isRestoringOrderLineItem]
    );

    const isOrderCanceledWithCanceledOrderReview = useMemo(
        () =>
            !!initValues?.canceledOn &&
            !!initValues?.orderReview &&
            initValues.orderReview.status === ReviewStatus.Canceled,
        [initValues?.canceledOn, initValues?.orderReview]
    );

    const additionalHeaderItems = useMemo(() => {
        if (initValues?.id) {
            return (
                <>
                    {!initValues?.canceledOn ? (
                        <AuthenticatedComponent
                            requiredPermissions={['cancel:orders']}
                            logic='and'
                            children={
                                <Button
                                    startIcon={<Cancel />}
                                    variant='contained'
                                    disabled={
                                        isCancelingOrder ||
                                        isCancelingOrderLineItem ||
                                        isRestoringOrder ||
                                        isRestoringOrderLineItem ||
                                        !!initValues?.canceledOn ||
                                        initValues?.isCancelable === false
                                    }
                                    onClick={() => {
                                        if (initValues?.id) {
                                            setIsCancelMenuOpen(true);
                                        }
                                    }}>
                                    Cancel Order
                                </Button>
                            }
                        />
                    ) : (
                        <AuthenticatedComponent
                            requiredPermissions={['cancel:orders']}
                            logic='and'
                            children={
                                <Button
                                    startIcon={<Restore />}
                                    variant='contained'
                                    disabled={
                                        isCancelingOrder ||
                                        isCancelingOrderLineItem ||
                                        isRestoringOrder ||
                                        isRestoringOrderLineItem ||
                                        !!initValues?.orderReview
                                    }
                                    onClick={() => {
                                        if (initValues?.id) {
                                            handleRestoreOrder();
                                        }
                                    }}>
                                    Restore Order
                                </Button>
                            }
                        />
                    )}
                    <Button
                        startIcon={<Download />}
                        variant='contained'
                        disabled={!initValues?.id || isDownloadingPdfs}
                        onClick={() => {
                            if (initValues?.id) {
                                handleDownload();
                            }
                        }}>
                        Download
                    </Button>
                    <AuthenticatedComponent
                        requiredPermissions={['read:order']}
                        children={
                            <Button
                                startIcon={<History />}
                                variant='contained'
                                disabled={!initValues?.id}
                                onClick={() => {
                                    if (initValues?.id) {
                                        setIsHistoryDialogOpen(true);
                                    }
                                }}>
                                View History
                            </Button>
                        }
                    />
                </>
            );
        }
    }, [handleDownload, handleRestoreOrder, initValues?.canceledOn, initValues?.id, initValues?.isCancelable, initValues?.orderReview, isCancelingOrder, isCancelingOrderLineItem, isDownloadingPdfs, isRestoringOrder, isRestoringOrderLineItem]);

    const getNotModifiableMessage = useCallback(() => {
        if (!!initValues?.canceledOn) {
            return <>canceled</>;
        }

        if (!!initValues?.orderReview) {
            return (
                <Link
                    variant='body2'
                    href={`/job/${jobId}/order/${currentOrderId}/orderReview/${initValues?.orderReview?.id}`}
                    onClick={(e) => {
                        e.preventDefault();
                        navigate(`/job/${jobId}/order/${currentOrderId}/orderReview/${initValues?.orderReview?.id}`);
                    }}>
                    in Review
                </Link>
            );
        }

        return <></>;
    }, [currentOrderId, initValues?.canceledOn, initValues?.orderReview, jobId, navigate]);

    const orderTitle = useMemo(() => {
        if (!initValues?.id) return 'New Order';
        const customerName = formCustomer?.name || '';
        const customerNumber = formCustomer?.customerNumber || '';
        return `${customerNumber}: ${customerName} (Order #${initValues?.orderNumber})`;
    }, [initValues?.id, initValues?.orderNumber, formCustomer?.name, formCustomer?.customerNumber]);

    const orderTabTitle = useMemo(() => {
        if (!initValues?.id) return 'New Order';
        const customerNumber = formCustomer?.customerNumber || '';
        return `Order #${initValues?.orderNumber}: Cust #${customerNumber}`;
    }, [initValues?.id, initValues?.orderNumber, formCustomer?.customerNumber]);

    if (isLoading || isJobLoading) {
        return <LoadingIndicator />;
    }

    return (
        <Box height='100%' sx={{ overflowY: 'hidden' }} display='flex' flexDirection='column' gap='16px'>
            {selectedSiteHaulingLineItem && (
                <QuickAddMaterialLineItemDialog
                    open={isQuickAddMaterialLineItemDialogOpen}
                    onClose={() => {
                        setIsQuickAddMaterialLineItemDialogOpen(false);
                    }}
                    onAdd={(lineItem) => {
                        handleAddNewLineItem(lineItem);
                        setIsQuickAddMaterialLineItemDialogOpen(false);
                    }}
                    siteHaulingLineItem={selectedSiteHaulingLineItem!}
                    getExcludedMaterialIdsForHaulingLineItem={getExcludedMaterialIdsForHaulingLineItem}
                    customerName={formCustomer?.name}
                />
            )}
            <ConfirmationDialogWithInput
                open={isCancelMenuOpen}
                onClose={() => {
                    setIsCancelMenuOpen(false);
                }}
                formLabel={'Please enter a cancelation reason.'}
                title={`Cancel Order`}
                titleIcon={<Cancel />}
                cancelText={'Cancel'}
                confirmText={'Save'}
                save={handleCancelOrder}
                requireConfirmation={false}
            />
            <ConfirmationDialogWithInput
                open={isCancelOrderLineItemMenuOpen}
                onClose={() => {
                    setIsCancelOrderLineItemMenuOpen(false);
                    setOrderLineItemIndexToModify(null);
                }}
                formLabel={'Please enter a cancelation reason.'}
                title={`Cancel Order Line Item`}
                titleIcon={<Cancel />}
                cancelText={'Cancel'}
                confirmText={'Save'}
                save={handleCancelOrderLineItem}
                requireConfirmation={false}
            />
            {renderAddMenu()}
            <SlimFormHeader
                objectName='Order'
                permissionsTypeName='order'
                icon={<ListAlt />}
                title={orderTitle}
                tabTitle={orderTabTitle}
                titleMaxWidth="60vw"
                breadcrumbs={[
                    { label: 'Home', navLink: '/' },
                    { label: 'Jobs', navLink: '/jobs' },
                    { label: `Job #${job?.jobNumber}`, navLink: `/job/${jobId}` },
                ]}
                isActive={formIsActive}
                handleIsActiveChange={handleIsActiveChange}
                id={currentOrderId}
                isFormDirty={isFormDirty}
                handleCancel={handleCancel}
                handleSave={handleSaveClicked}
                entityNameSingular={'order'}
                deleteMutation={useArchiveOrderDeleteByIdMutation}
                canDelete={!!currentOrderId && !isNotModifiable}
                canSave={!!formQuote && !isNotModifiable}
                saveButtonTooltip={formQuote ? undefined : 'Set the Order Date and save to continue modifying this Order.'}
                additionalHeaderItems={additionalHeaderItems}
            />
            <Paper sx={{ display: 'flex', p: 2, mx: 2, alignItems: 'center' }}>
                <AuthenticatedComponent
                    requiredPermissions={['read:quote']}
                    children={
                        <>
                            {initValues &&
                                <OrderRelatedLinks order={initValues} />
                            }
                        </>
                    }
                />
            </Paper>
            <Box flexGrow={1} display='flex' flexDirection='column' px={2} pb={1} gap={2} overflow='hidden'>
                {isNotModifiable && (
                    <AuthenticatedComponent
                        requiredPermissions={['edit:review', 'edit:billing']}
                        logic='or'
                        children={
                            <Paper square variant='outlined' sx={{ p: 1, textAlign: 'center' }}>
                                {isOrderCanceledWithCanceledOrderReview ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                                        <Typography>
                                            This order is canceled and can&apos;t be edited. It was canceled together with the{' '}
                                            <Link
                                                variant='body2'
                                                href={`/job/${jobId}/order/${currentOrderId}/orderReview/${initValues?.orderReview?.id}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    navigate(
                                                        `/job/${jobId}/order/${currentOrderId}/orderReview/${initValues?.orderReview?.id}`
                                                    );
                                                }}>
                                                Order Review
                                            </Link>
                                            , which is also in a canceled state
                                        </Typography>
                                        <Typography>
                                            To restore this order, open the order review and use <strong>Restore</strong> there first
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Typography>This order is {getNotModifiableMessage()} and can&apos;t be edited.</Typography>
                                )}
                            </Paper>
                        }
                    />
                )}
                {!currentOrderId && (
                    <Paper square variant='outlined' sx={{ p: 1, textAlign: 'center' }}>
                        <Typography>
                            <span style={{ color: '#A3271F' }}>Save this Order</span> with your Order Date to link it to the Current Quote on that date.
                        </Typography>
                    </Paper>
                )}
                {formQuote && (
                    <Box>
                        <StandardCardWithHeader headerTitle='Quote Price List' headerIcon={<ListAlt />}>
                            <Grid container direction='column' pb={2}>
                                <Grid item container direction='row' px={2} justifyContent='space-between' alignItems='center'>
                                    <Grid item>
                                        <Tooltip title='Quick Add All'>
                                            <IconButton
                                                onClick={(event) => {
                                                    if (event) {
                                                        handleAddNewLineItems(formQuote ? formQuote?.lineItems : []);
                                                    }
                                                }}
                                                aria-label='quick add all'
                                                sx={{
                                                    marginRight: 2,
                                                    color: theme.palette.primary.contrastText,
                                                    backgroundColor: theme.palette.primary.main,
                                                    '&:hover': { backgroundColor: theme.palette.primary.dark },
                                                }}
                                                disabled={!currentOrderId || isNotModifiable}>
                                                <PlaylistAdd />
                                            </IconButton>
                                        </Tooltip>
                                        <Button variant='contained' onClick={handleAddMenuOpen} disabled={!currentOrderId || isNotModifiable}>
                                            Add New Line item to Quote
                                        </Button>
                                    </Grid>
                                </Grid>
                                <Grid item>
                                    {formQuote && (
                                        <QuoteLineItemsOrderView
                                            quote={formQuote}
                                            copyQuoteLineItemToOrder={handleAddNewLineItem}
                                            disableCopy={!currentOrderId || isNotModifiable}
                                            maxHeight={'25vh'} // should be a string with 'px' or 'vh'
                                        />
                                    )}
                                </Grid>
                            </Grid>
                        </StandardCardWithHeader>
                    </Box>
                )}
                <Paper sx={{ display: 'flex', flexGrow: 1, overflow: 'auto', flexDirection: 'column', p: 2 }}>
                    <Grid container direction='row' spacing={2} alignItems='center'>
                        <Grid item xs={12} sm={3} md={1}>
                            <FormInput value={formOrderNumber?.toString()} onChange={() => { }} label='Order #' fullWidth disabled required />
                        </Grid>
                        <Grid item xs={12} sm={3} md={2}>
                            <ContactSelect
                                label='Ordered By'
                                customerId={formCustomer?.id}
                                selectedContactId={formOrderedByContact?.id ?? ''}
                                handleSelectedContactChange={handleOrderedByContactChange}
                                errorMessage={fieldErrors.get('orderedByContactId')}
                                disabled={isNotModifiable}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3} md={2}>
                            <ContactSelect
                                customerId={formCustomer?.id}
                                selectedContactId={formContact?.id ?? ''}
                                handleSelectedContactChange={handleContactChange}
                                errorMessage={fieldErrors.get('contactId')}
                                disabled={isNotModifiable}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3} md={1.5}>
                            <FormInput
                                value={formPoNumber}
                                onChange={(event) => handlePoNumberChange(event.target.value)}
                                label='PO Number'
                                fullWidth
                                required
                                errorText={fieldErrors.get('poNumber')}
                                disabled={isNotModifiable}
                                maxLength={30}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3} md={1.5}>
                            <FormInput
                                value={formJobNumber}
                                onChange={(event) => handleJobNumberChange(event.target.value)}
                                label='Job Number'
                                fullWidth
                                disabled={isNotModifiable}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3} md={1}>
                            <FormControl fullWidth disabled={isNotModifiable}>
                                <FormControlLabel
                                    control={<Checkbox disabled={isNotModifiable} checked={formIsTaxable} onChange={handleIsTaxableChange} />}
                                    label='Is Taxable'
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                    <Typography variant='h2' mt={1}>Order Line Items</Typography>
                    <Box flexGrow={1} overflow='hidden'>
                        <OrderLineItemTable
                            quoteMemo={formQuote?.memo ?? ''}
                            orderLineItems={formOrderLineItems}
                            removeLineItem={(i) => handleRemoveLineItem(i)}
                            setOrderLineItems={handleOrderLineItemsChange}
                            isReadOnly={isNotModifiable}
                            fieldErrors={lineItemFieldErrors}
                            handleCancelOrderLineItem={(index) => {
                                setOrderLineItemIndexToModify(index);
                                setIsCancelOrderLineItemMenuOpen(true);
                            }}
                            handleRestoreOrderLineItem={(index) => {
                                setOrderLineItemIndexToModify(index);
                                handleRestoreOrderLineItem();
                            }}
                            customer={formCustomer}
                            isFob={formQuote?.type === QuoteType.FOB}
                            orderPoNumber={formPoNumber}
                            orderJobNumber={formJobNumber}
                            formOrderDate={formOrderDate}
                            orderReviewLineItems={initValues?.orderReview?.orderLineItems}
                            isOrderTaxable={formIsTaxable}
                        />
                    </Box>
                </Paper>
            </Box>
            {showAffectedDispatchesDialog &&
                <AffectedDispatchesDialog
                    open={showAffectedDispatchesDialog}
                    orderLineItems={formOrderLineItems}
                    onCancel={handleAffectedDispatchesDialogCancel}
                    onSuccess={handleAffectedDispatchesDialogSuccess}
                    isLoading={isGettingAffectedDispatches}
                    affectedDispatches={affectedDispatches}
                />
            }
            {currentOrderId && (
                <OrderHistoryDialog
                    open={isHistoryDialogOpen}
                    orderId={currentOrderId}
                    onClose={() => setIsHistoryDialogOpen(false)}
                />
            )}
        </Box>
    );
};
